import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';

// Keyboard shortcuts context
const KeyboardShortcutsContext = createContext();

// Custom hook to use keyboard shortcuts
export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider');
  }
  return context;
};

// Utility function to check if an element is an input field
const isInputElement = (element) => {
  const inputTypes = ['input', 'textarea', 'select'];
  const contentEditables = element.contentEditable === 'true';
  return inputTypes.includes(element.tagName.toLowerCase()) || contentEditables;
};

// Utility function to get key combination string
const getKeyCombo = (event) => {
  const keys = [];
  
  if (event.ctrlKey) keys.push('ctrl');
  if (event.metaKey) keys.push('cmd');
  if (event.shiftKey) keys.push('shift');
  if (event.altKey) keys.push('alt');
  
  // Add the main key
  const key = event.key.toLowerCase();
  if (!['control', 'meta', 'shift', 'alt'].includes(key)) {
    keys.push(key);
  }
  
  return keys.join('+');
};

// Default keyboard shortcuts
const DEFAULT_SHORTCUTS = {
  // Global shortcuts
  'ctrl+n': { action: 'newOrder', description: 'Create new order', global: true },
  'cmd+n': { action: 'newOrder', description: 'Create new order', global: true },
  'ctrl+s': { action: 'saveDraft', description: 'Save draft order', global: true },
  'cmd+s': { action: 'saveDraft', description: 'Save draft order', global: true },
  'ctrl+p': { action: 'printTicket', description: 'Print work ticket', global: true },
  'cmd+p': { action: 'printTicket', description: 'Print work ticket', global: true },
  'ctrl+f': { action: 'searchCustomers', description: 'Search customers', global: true },
  'cmd+f': { action: 'searchCustomers', description: 'Search customers', global: true },
  'ctrl+enter': { action: 'submitOrder', description: 'Submit order', global: false },
  'cmd+enter': { action: 'submitOrder', description: 'Submit order', global: false },
  '?': { action: 'showHelp', description: 'Show help/shortcuts', global: true },
  
  // Navigation shortcuts
  'escape': { action: 'closeModal', description: 'Close modal/dialog', global: true },
  'f2': { action: 'editSelected', description: 'Edit selected item', global: false },
  'delete': { action: 'deleteSelected', description: 'Delete selected item', global: false },
  'backspace': { action: 'deleteSelected', description: 'Delete selected item', global: false },
  'enter': { action: 'selectFirst', description: 'Select first search result', global: false },
  
  // Dashboard shortcuts
  'alt+1': { action: 'viewCreateOrder', description: 'View create order', global: true },
  'alt+2': { action: 'viewOrders', description: 'View orders list', global: true },
  'alt+3': { action: 'viewCustomers', description: 'View customers', global: true },
  'alt+d': { action: 'viewDashboard', description: 'View dashboard', global: true },
};

// Keyboard shortcuts provider component
export const KeyboardShortcutsProvider = ({ children }) => {
  const [shortcuts, setShortcuts] = useState(DEFAULT_SHORTCUTS);
  const [handlers, setHandlers] = useState({});
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [activeElement, setActiveElement] = useState(null);

  // Register a shortcut handler
  const registerHandler = useCallback((action, handler) => {
    setHandlers(prev => ({
      ...prev,
      [action]: handler
    }));
  }, []);

  // Unregister a shortcut handler
  const unregisterHandler = useCallback((action) => {
    setHandlers(prev => {
      const newHandlers = { ...prev };
      delete newHandlers[action];
      return newHandlers;
    });
  }, []);

  // Add a custom shortcut
  const addShortcut = useCallback((keyCombo, action, description, global = false) => {
    setShortcuts(prev => ({
      ...prev,
      [keyCombo]: { action, description, global }
    }));
  }, []);

  // Remove a shortcut
  const removeShortcut = useCallback((keyCombo) => {
    setShortcuts(prev => {
      const newShortcuts = { ...prev };
      delete newShortcuts[keyCombo];
      return newShortcuts;
    });
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    const keyCombo = getKeyCombo(event);
    const shortcut = shortcuts[keyCombo];
    
    if (!shortcut) return;
    
    const isInInput = isInputElement(event.target);
    const handler = handlers[shortcut.action];
    
    // Check if shortcut should be handled
    const shouldHandle = shortcut.global || !isInInput;
    
    if (shouldHandle && handler) {
      // Special cases for certain shortcuts
      if (shortcut.action === 'printTicket' || shortcut.action === 'saveDraft') {
        event.preventDefault();
      }
      
      // Prevent default for global shortcuts when not in input
      if (shortcut.global && !isInInput) {
        event.preventDefault();
      }
      
      // Handle the shortcut
      try {
        handler(event, keyCombo);
      } catch (error) {
        console.error('Error handling shortcut:', shortcut.action, error);
      }
    }
  }, [shortcuts, handlers]);

  // Show/hide help modal
  const toggleHelp = useCallback(() => {
    setIsHelpVisible(prev => !prev);
  }, []);

  const showHelp = useCallback(() => {
    setIsHelpVisible(true);
  }, []);

  const hideHelp = useCallback(() => {
    setIsHelpVisible(false);
  }, []);

  // Track active element for context-sensitive shortcuts
  useEffect(() => {
    const handleFocus = (event) => {
      setActiveElement(event.target);
    };
    
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  // Set up global event listeners
  useEffect(() => {
    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Register default help handler (separate effect to avoid infinite loop)
  useEffect(() => {
    const helpHandler = () => toggleHelp();
    setHandlers(prev => ({ ...prev, showHelp: helpHandler }));
  }, [toggleHelp]);

  // Context value
  const contextValue = {
    shortcuts,
    handlers,
    activeElement,
    isHelpVisible,
    registerHandler,
    unregisterHandler,
    addShortcut,
    removeShortcut,
    showHelp,
    hideHelp,
    toggleHelp
  };

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

// Hook to register shortcut handlers
export const useShortcutHandler = (action, handler, dependencies = []) => {
  const { registerHandler, unregisterHandler } = useKeyboardShortcuts();
  
  useEffect(() => {
    if (handler) {
      registerHandler(action, handler);
    }
    
    return () => {
      unregisterHandler(action);
    };
  }, [action, handler, registerHandler, unregisterHandler, ...dependencies]);
};

// Hook to register multiple shortcut handlers
export const useShortcutHandlers = (handlerMap, dependencies = []) => {
  const { registerHandler, unregisterHandler } = useKeyboardShortcuts();
  
  useEffect(() => {
    // Register all handlers
    Object.entries(handlerMap).forEach(([action, handler]) => {
      if (handler) {
        registerHandler(action, handler);
      }
    });
    
    // Cleanup function
    return () => {
      Object.keys(handlerMap).forEach(action => {
        unregisterHandler(action);
      });
    };
  }, [registerHandler, unregisterHandler, handlerMap, ...dependencies]);
};

// Utility function to get shortcut display text
export const getShortcutDisplay = (keyCombo) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  return keyCombo
    .split('+')
    .map(key => {
      switch (key) {
        case 'ctrl':
          return isMac ? '⌃' : 'Ctrl';
        case 'cmd':
          return '⌘';
        case 'shift':
          return '⇧';
        case 'alt':
          return isMac ? '⌥' : 'Alt';
        case 'enter':
          return '↵';
        case 'escape':
          return 'Esc';
        case 'delete':
          return 'Del';
        case 'backspace':
          return '⌫';
        case ' ':
          return 'Space';
        default:
          return key.toUpperCase();
      }
    })
    .join(isMac ? '' : '+');
};

export default KeyboardShortcutsProvider;